import type { ReactNode } from 'react';
import { XIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { useDockedHeight } from '../../shared/results_page/docked_preview.js';
import { OfferBadge } from '../../shared/results_page/offer_badge.js';
import { OutOfPolicyGlyph } from '../../policy/out_of_policy_mark.js';
import type { RentalOfferView } from '../models.js';
import { RentalCompanyLogo } from '../rental_company_mark.js';
import { RentalPhoto, RentalSpecs, type RentalOfferCardCopy } from './rental_offer_card.js';
import './rental_map_preview.css';

export type RentalMapPreviewCopy = {
  /** O X da folha ("Fechar o carro selecionado"). */
  closeLabel: string;
  /** A linha de local quando retira e devolve na mesma agência ("Retirada e
   *  devolução"); em agências diferentes a linha usa o `pickupLabel` do card. */
  sameStopLabel: string;
};

export type RentalMapPreviewProps = {
  offer: RentalOfferView;
  copy: RentalMapPreviewCopy;
  cardCopy: RentalOfferCardCopy;
  /** A distância do pé do mapa até a folha: a folha da lista mais o vão. */
  bottom: number;
  /** Uma escrita em voo (a cotação, a reserva): o toque não seleciona. */
  busy: boolean;
  onSelect(offerId: string): void;
  onClose(): void;
  /** A altura viva da folha (`undefined` ao sair de cena), para o mapa tirar
   *  os controles de vista de baixo dela. */
  onResize?: (height: number | undefined) => void;
};

/**
 * A folha do CARRO SELECIONADO no mapa do celular (#5125, #3995): o toque no
 * pino de uma agência com uma oferta só abre esta folha ancorada no pé do
 * mapa, logo acima da folha da lista — o molde da prévia do hotel (#3108).
 * Fundo branco, foto da categoria com a logo num chip, categoria e modelos,
 * specs, a agência e o preço. Sem insight de IA (lacuna de dado da #5125).
 *
 * O gesto é o mesmo do card da lista, `Selecionar`: um botão que cobre a folha
 * inteira (o toque em qualquer ponto seleciona) e se chama como o do card. O X
 * fica acima dele e só fecha a folha — o filtro do pino continua.
 */
export function RentalMapPreview({
  offer,
  copy,
  cardCopy,
  bottom,
  busy,
  onSelect,
  onClose,
  onResize,
}: Readonly<RentalMapPreviewProps>): ReactNode {
  const ref = useDockedHeight(onResize);
  const title = [offer.categoryLabel, offer.vehicleLabel].filter(Boolean).join(' · ');
  const sameStop = offer.pickup.agencyLabel === offer.dropoff.agencyLabel;
  const [badge] = offer.badges;
  return (
    <article ref={ref} className="hui-rental-map-preview" style={{ bottom }} aria-label={title}>
      <div className="hui-rental-map-preview-media">
        <RentalPhoto url={offer.thumbnailUrl} />
        {badge && (
          <div className="hui-rental-map-preview-badge">
            <OfferBadge badge={badge} />
          </div>
        )}
        {/* Sem logo, o nome da locadora (a queda do `RentalCompanyLogo`) vai
            direto no chip branco, sem o fundo do chip de texto do card. */}
        <span className="hui-rental-map-preview-logo [&>span]:h-auto [&>span]:bg-transparent [&>span]:px-0">
          <RentalCompanyLogo company={offer.company} />
        </span>
      </div>
      <div className="hui-rental-map-preview-info">
        <h3 className="hui-rental-map-preview-title">
          <strong>{offer.categoryLabel}</strong>
          {offer.vehicleLabel && (
            <>
              {' · '}
              <span>{offer.vehicleLabel}</span>
            </>
          )}
        </h3>
        <p className="hui-rental-map-preview-price">
          <strong>
            {offer.outOfPolicyLabel && <OutOfPolicyGlyph label={offer.outOfPolicyLabel} />}
            {offer.priceLabel}
          </strong>
          {offer.priceDetail && <small>{offer.priceDetail}</small>}
        </p>
        <RentalSpecs offer={offer} copy={cardCopy} />
      </div>
      {/* A agência reticencia; o papel do ponto ("Retirada e devolução") não. */}
      <p className="hui-rental-map-preview-stop" title={offer.pickup.addressLabel}>
        <span>{offer.pickup.agencyLabel}</span>
        {` · ${sameStop ? copy.sameStopLabel : cardCopy.pickupLabel}`}
      </p>
      {offer.selectable && (
        <button
          type="button"
          className="hui-rental-map-preview-select"
          aria-label={`${cardCopy.selectLabel} — ${title}`}
          disabled={busy}
          onClick={() => {
            onSelect(offer.id);
          }}
        />
      )}
      <Button
        type="button"
        variant="outline"
        size="icon-sm"
        className="hui-rental-map-preview-close rounded-full shadow-sm"
        aria-label={copy.closeLabel}
        onClick={onClose}
      >
        <XIcon aria-hidden="true" />
      </Button>
    </article>
  );
}
